import { useId, useState } from 'react';
import { type ArtefactKind, currentArtefacts, type SourceArtefact } from '@zevik/schemas';
import type { Source } from '../api/client';
import { uploadArtefact } from '../api/pipeline';
import { apiSentence, sessionLost } from '../credential/session';
import { KIND_LABELS, KINDS, planUpload } from '../upload/kind';
import { Refused } from './credential/CredentialShell';

/**
 * The assessment's documents, and a way to add one
 * (docs/DECISIONS.md ZD-124 §3).
 *
 * The list is every artefact `GET /:id/artefacts` returns; which of them are
 * current is `currentArtefacts`' answer — the rule ingestion reads by, from
 * `@zevik/schemas`, so the screen and the run agree on what a replaced
 * document is. A replaced one stays listed and says so, because nothing
 * deletes an artefact and a person should see what they superseded.
 *
 * Choosing a file suggests a kind from its type (`upload/kind.ts`) and the
 * person confirms or changes it before sending; the store checks the pair and
 * refuses a mismatch in its own words. An upload never starts a run
 * (ZD-121 §5.1): the run control is a separate act, and it is the person
 * saying the documents are complete.
 *
 * Declared rather than missing: a document cannot be removed here, and a
 * replacement — an upload naming the document it supersedes — is not offered
 * on this screen yet.
 */
export function Documents({
  source,
  assessmentId,
  artefacts,
  upload,
  onUploaded,
  onSessionLost,
}: {
  source: Source;
  assessmentId: string;
  artefacts: SourceArtefact[];
  /** Whether `next` says a document may be added from here. */
  upload: boolean;
  onUploaded: (warning: string | null) => void | Promise<void>;
  onSessionLost: (reason: string) => void;
}): React.JSX.Element {
  const current = new Set(currentArtefacts(artefacts).map((artefact) => artefact.artefact_id));
  return (
    <section className="documents" aria-label="Documents">
      <h2>Documents</h2>
      {artefacts.length === 0 ? (
        <p className="documents__lead">
          No documents yet. Add the architecture description and any diagrams or notes that describe
          the system; when they are all here, start the analysis below.
        </p>
      ) : (
        <ul className="documents__list">
          {artefacts.map((artefact) => (
            <li key={artefact.artefact_id} data-current={current.has(artefact.artefact_id)}>
              <span className="documents__name">{artefact.filename}</span>
              <span className="documents__meta">
                {KIND_LABELS[artefact.kind]} · {formatBytes(artefact.size_bytes)} ·{' '}
                <time dateTime={artefact.uploaded_at}>
                  {new Date(artefact.uploaded_at).toLocaleString()}
                </time>
                {current.has(artefact.artefact_id) ? null : ' · replaced by a later upload'}
              </span>
            </li>
          ))}
        </ul>
      )}
      {upload ? (
        <UploadForm
          source={source}
          assessmentId={assessmentId}
          onUploaded={onUploaded}
          onSessionLost={onSessionLost}
        />
      ) : null}
      <p className="limit">
        A document cannot be removed from here, and replacing one — an upload that names the
        document it supersedes — is not on this screen yet.
      </p>
    </section>
  );
}

function UploadForm({
  source,
  assessmentId,
  onUploaded,
  onSessionLost,
}: {
  source: Source;
  assessmentId: string;
  onUploaded: (warning: string | null) => void | Promise<void>;
  onSessionLost: (reason: string) => void;
}): React.JSX.Element {
  const fileId = useId();
  const kindId = useId();
  const [file, setFile] = useState<File | null>(null);
  const [mediaType, setMediaType] = useState<string | null>(null);
  const [kind, setKind] = useState<ArtefactKind | ''>('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const choose = (chosen: File | null): void => {
    setRefusal(null);
    setFile(chosen);
    setMediaType(null);
    setKind('');
    if (chosen === null) return;
    try {
      const plan = planUpload(chosen);
      setMediaType(plan.media_type);
      setKind(plan.kind ?? '');
    } catch (error) {
      setFile(null);
      setRefusal(error instanceof Error ? error.message : String(error));
    }
  };

  const submit = async (): Promise<void> => {
    if (file === null || mediaType === null || kind === '') return;
    setRefusal(null);
    setSending(true);
    try {
      const outcome = await uploadArtefact(source, assessmentId, {
        file,
        kind,
        media_type: mediaType,
      });
      setFile(null);
      setMediaType(null);
      setKind('');
      await onUploaded(outcome.warning);
    } catch (error) {
      if (sessionLost(error)) onSessionLost(apiSentence(error));
      else setRefusal(apiSentence(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      className="upload"
      aria-label="Add a document"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="field" htmlFor={fileId}>
        Add a document
      </label>
      <input
        id={fileId}
        type="file"
        onChange={(event) => {
          choose(event.target.files?.[0] ?? null);
          // So the same file can be chosen again after a refusal.
          event.target.value = '';
        }}
      />
      {file === null ? null : (
        <>
          <p className="upload__chosen">
            {file.name} · {formatBytes(file.size)} · {mediaType}
          </p>
          <label className="field" htmlFor={kindId}>
            Upload it as
          </label>
          <select
            id={kindId}
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as ArtefactKind | '');
            }}
          >
            {kind === '' ? <option value="">Choose what this document is…</option> : null}
            {KINDS.map((known) => (
              <option key={known} value={known}>
                {KIND_LABELS[known]}
              </option>
            ))}
          </select>
        </>
      )}
      {refusal === null ? null : <Refused message={refusal} />}
      {file === null ? null : (
        <div className="upload__actions">
          <button type="submit" className="submit" disabled={sending || kind === ''}>
            {sending ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      )}
    </form>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
